import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { useDebouncedValue } from '../useDebouncedValue';
import {
  GongFlowBrowseResponseSchema,
  GongFlowSuggestedResponseSchema,
  GongFlowPreferenceResponseSchema,
  GongFlowStepsApiResponseSchema,
  filterAndSortGongFlows,
  type GongFlowBrowseResponse,
  type GongFlowBrowseSort,
  type GongFlowPreferenceState,
  type GongFlowStepsApiResponse,
  type GongFlowSummary,
} from '@shared/flow/gong-flow-catalog';

/**
 * Gong Flow browse/suggested/pin-hide fetch hooks (U12, Gong Flow Play +
 * Queue redesign) — the "data-fetching hooks live in/near EnrollQueueContext"
 * half of the picker's Gong group + browse surface. Sibling to
 * `requests-hooks.ts` (same shape: query-key exports + hooks, consumed both
 * by `EnrollQueueContext` — the suggested-3 query, part of its plays fan-out
 * — and directly by `PlayPicker`'s connected browse wrapper, which is NOT
 * itself part of the always-mounted context fan-out since it only exists
 * while the browse overlay is open).
 *
 * Cache-indefinitely + invalidate-on-mutation (CLAUDE.md): `staleTime:
 * Infinity` on both reads; `useSetGongFlowPreference`'s `onSuccess`
 * invalidates BOTH query-key families, since a pin/hide change must be
 * reflected in the picker's suggested-3 group AND the open browse list
 * immediately (bead acceptance criteria — "hide removes a flow from browse +
 * picker immediately").
 *
 * Round-5 QA follow-ups (`docs/flow/gong-flow-play-qa-followups-2026-08-20.md`
 * §2, plan `2026-08-20-001`): every browse keystroke used to key a fresh
 * `useQuery` (`search` was IN the query key) and re-drive a live Gong fetch +
 * ~660 serial mirror upserts server-side — a real production-load bug, not
 * just a UX one. `useGongFlowsBrowse` now fetches the FULL catalog ONCE per
 * browse-open (static query key, `refetchOnMount: 'always'`) and
 * `useGongFlowBrowseController` filters/sorts that cached array client-side,
 * per (debounced) keystroke, via the SAME `filterAndSortGongFlows` the server
 * runs (`@shared/flow/gong-flow-catalog`) — this file's earlier "no
 * client-side full-catalog search" note (and `PlayPicker.tsx`'s, guarding a
 * DIFFERENT thing — not rendering gong_flow's ~660 exposure rows as
 * individual picker rows) is a deliberate, cited reversal for the browse
 * overlay specifically, not a re-opening of that other rule.
 */

export const FLOW_GONG_SUGGESTED_KEY = ['flow-gong-flows-suggested'] as const;
export const FLOW_GONG_BROWSE_KEY = ['flow-gong-flows-browse'] as const;

/** GET /api/flow/gong/flows/suggested — Tier-1 ranking, 0-3 entries, no fallback (see the wire schema's doc comment). */
export function useGongFlowsSuggested() {
  const { apiFetch } = useApi();
  return useQuery<GongFlowSummary[]>({
    queryKey: FLOW_GONG_SUGGESTED_KEY,
    queryFn: async () => {
      const parsed = await fetchJson(apiFetch, GongFlowSuggestedResponseSchema, '/api/flow/gong/flows/suggested');
      return parsed.flows;
    },
    staleTime: Infinity,
  });
}

/**
 * GET /api/flow/gong/flows — the full available catalog, no query params
 * (round-5 plan Decision #1). Static query key: this is ONE fetch per
 * browse-open, not one per filter/keystroke — `useGongFlowBrowseController`
 * owns search/folder/sort as local state and filters the cached `flows`
 * array client-side (`filterAndSortGongFlows`). `refetchOnMount: 'always'`
 * (Decision #7) re-drives the live Gong fetch + mirror write each time the
 * browse panel opens (`ConnectedGongFlowBrowse` only mounts this hook while
 * open, `PlayPicker.tsx`), while `staleTime: Infinity` means the PREVIOUS
 * open's cached rows render instantly rather than the panel showing a blank
 * loading state while the refresh lands.
 */
export function useGongFlowsBrowse() {
  const { apiFetch } = useApi();
  return useQuery<GongFlowBrowseResponse>({
    queryKey: FLOW_GONG_BROWSE_KEY,
    queryFn: () => fetchJson(apiFetch, GongFlowBrowseResponseSchema, '/api/flow/gong/flows'),
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}

export const FLOW_GONG_STEPS_KEY = ['flow-gong-flow-steps'] as const;

/** Shared fetch+validate for GET /api/flow/gong/flows/:flowId/steps — the one place both the cached hook and the plain imperative call parse the response.
 * `force` (R5a/U5) appends `?force=true`, matching `getGongFlowSteps`'s
 * `force` param and `flow-gong.ts`'s `req.query.force === 'true'`
 * convention — bypasses the never-invalidated steps cache and always
 * live-fetches from Gong. */
function fetchGongFlowStepsRaw(
  apiFetch: ReturnType<typeof useApi>['apiFetch'],
  flowId: string,
  force = false,
): Promise<GongFlowStepsApiResponse> {
  const url = `/api/flow/gong/flows/${encodeURIComponent(flowId)}/steps${force ? '?force=true' : ''}`;
  return fetchJson(apiFetch, GongFlowStepsApiResponseSchema, url);
}

/**
 * GET /api/flow/gong/flows/:flowId/steps (U13, portable step-override
 * editor) — the flow's default per-step copy the editor seeds an unset
 * override from, plus `flow_owner_name` for the owner note. `flowId: null`
 * (a non-gong_flow item, or one with no play assigned yet) disables the
 * query entirely rather than firing a request that would 400.
 */
export function useGongFlowSteps(flowId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<GongFlowStepsApiResponse>({
    queryKey: [...FLOW_GONG_STEPS_KEY, flowId],
    queryFn: () => fetchGongFlowStepsRaw(apiFetch, flowId as string),
    enabled: flowId !== null,
    // The step cache has no TTL server-side either (steps only change via a
    // rep-triggered live-fetch-on-miss or the daily sync job) — cache
    // indefinitely (CLAUDE.md caching rule), no invalidation point exists
    // for this read today.
    staleTime: Infinity,
  });
}

/**
 * FORCED variant of `useGongFlowSteps` (R5a/U5) — used at the moments live
 * step data matters most: opening the step-override editor
 * (`EnrollmentItemEditor.tsx`'s `GongFlowStepsSection`). The `force` flag is
 * folded INTO the query key (not just the fetch) — reusing the plain hook's
 * key with `?force=true` bolted on only in the fetch would mean a warm
 * `staleTime: Infinity` cache entry from the unforced hook (or a prior mount
 * of this one) short-circuits before `queryFn` ever runs, so the param would
 * never reach the network. `refetchOnMount: 'always'` (mirroring
 * `useGongFlowsBrowse`'s combination) is what actually re-drives the fetch
 * on every editor mount/remount.
 */
export function useGongFlowStepsForced(flowId: string | null) {
  const { apiFetch } = useApi();
  return useQuery<GongFlowStepsApiResponse>({
    queryKey: [...FLOW_GONG_STEPS_KEY, flowId, 'forced'],
    queryFn: () => fetchGongFlowStepsRaw(apiFetch, flowId as string, true),
    enabled: flowId !== null,
    staleTime: Infinity,
    refetchOnMount: 'always',
  });
}

/**
 * Imperative counterpart to `useGongFlowSteps`, for the one call site
 * (`PlayPicker`'s pre-assign auto-email gate) that needs the answer at
 * click time rather than render time. A PLAIN fetch, not routed through
 * `queryClient.fetchQuery` — `PlayPicker` mounts in several tests with no
 * `QueryClientProvider` ancestor at all (`ConnectedGongFlowBrowse`'s own doc
 * comment notes the same constraint), so this can't call `useQueryClient()`.
 * Trades away sharing `useGongFlowSteps`'s cache for that — an extra
 * network round-trip on the rare double-check (assign gate, then the
 * step-override editor) is a fine price for not adding a react-query
 * dependency to every `PlayPicker` mount. Always forced (R5a/U5): assigning
 * a play is exactly one of the two moments live step data matters most, so
 * this always appends `?force=true`.
 */
export function fetchGongFlowSteps(apiFetch: ReturnType<typeof useApi>['apiFetch'], flowId: string) {
  return fetchGongFlowStepsRaw(apiFetch, flowId, true);
}

/** PUT /api/flow/gong/flows/:flowId/preference — pin/hide, `state: null` clears. */
export function useSetGongFlowPreference() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, state }: { flowId: string; state: GongFlowPreferenceState | null }) =>
      fetchJson(apiFetch, GongFlowPreferenceResponseSchema, `/api/flow/gong/flows/${encodeURIComponent(flowId)}/preference`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_GONG_SUGGESTED_KEY });
      void queryClient.invalidateQueries({ queryKey: FLOW_GONG_BROWSE_KEY });
    },
  });
}

/**
 * POST-like force-fetch used by the browse panel's manual refresh button
 * (R5b/U7) — GET /api/flow/gong/flows?force=true, the SAME route as the
 * plain browse fetch with the U6 `?force=true` param appended, bypassing
 * the 15-minute `GONG_FLOW_BROWSE_TTL_MS` freshness gate server-side.
 * Modeled on `useSetGongFlowPreference`'s onSuccess-invalidate pattern
 * (:207 above) rather than writing the forced response straight into the
 * query cache: invalidating `FLOW_GONG_BROWSE_KEY` lets the already-mounted
 * `useGongFlowsBrowse` query re-fetch and take over normally, so the forced
 * response and the cache never diverge in shape (one parse path, one
 * schema). `useMutation` (not a second `useQuery`) because this is an
 * imperative, user-triggered action with no cache key of its own — exactly
 * `useSetGongFlowPreference`'s shape.
 */
function useForceRefreshGongFlows() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchJson(apiFetch, GongFlowBrowseResponseSchema, '/api/flow/gong/flows?force=true'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_GONG_BROWSE_KEY });
    },
  });
}

/** One row's derived UI state — GongFlowBrowse.tsx renders this, unaware of the mutation/query plumbing. */
export interface GongFlowBrowseController {
  search: string;
  onSearchChange: (value: string) => void;
  folder: string | null;
  onFolderChange: (folder: string | null) => void;
  sort: GongFlowBrowseSort;
  onSortChange: (sort: GongFlowBrowseSort) => void;
  flows: GongFlowSummary[];
  folders: string[];
  stale: boolean;
  isLoading: boolean;
  isError: boolean;
  onPin: (flow: GongFlowSummary) => void;
  onHide: (flow: GongFlowSummary) => void;
  /** Manual refresh escape hatch (R5b/U7) — forces a live re-fetch past the U6 TTL. */
  onRefresh: () => void;
  /** True for the duration of the forced refresh request — drives the button's disabled/spinner state (double-press must not stack requests). */
  isRefreshing: boolean;
  /** True when the most recent forced refresh failed. Non-blocking: `flows` still reflects the last-good cached list (the failed refresh is never written into the cache). */
  refreshError: boolean;
}

/**
 * The browse overlay's connected controller — owns local UI state (search,
 * folder, sort) and composes `useGongFlowsBrowse` (one fetch of the full
 * catalog per browse-open) + `useSetGongFlowPreference`. Round-5 plan U2:
 * there is no cursor/pagination state anymore (Decision #1 dropped server
 * pagination entirely) — `flows` is derived client-side by running the SAME
 * `filterAndSortGongFlows` the server runs over the cached catalog, debounced
 * 250ms on `search` (Decision #8, `GlobalSearch.tsx`'s sibling pattern) so
 * the input itself stays instant while the (already-in-memory) recompute
 * settles after typing pauses. Kept out of `GongFlowBrowse.tsx` itself so
 * that component stays presentational/provider-free per the bead's
 * architecture note (renders in tests without a QueryClientProvider).
 */
export function useGongFlowBrowseController(): GongFlowBrowseController {
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState<string | null>(null);
  const [sort, setSort] = useState<GongFlowBrowseSort>('recent');
  const debouncedSearch = useDebouncedValue(search, 250);

  const query = useGongFlowsBrowse();
  const prefMutation = useSetGongFlowPreference();
  const refreshMutation = useForceRefreshGongFlows();

  const flows = useMemo(
    () => filterAndSortGongFlows(query.data?.flows ?? [], { folder, search: debouncedSearch, sort }),
    [query.data, folder, debouncedSearch, sort],
  );

  return {
    search,
    onSearchChange: setSearch,
    folder,
    onFolderChange: setFolder,
    sort,
    onSortChange: setSort,
    flows,
    folders: query.data?.folders ?? [],
    stale: query.data?.stale ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    onPin: (flow) => {
      void prefMutation.mutateAsync({ flowId: flow.flow_id, state: flow.pinned ? null : 'pinned' });
    },
    onHide: (flow) => {
      void prefMutation.mutateAsync({ flowId: flow.flow_id, state: 'hidden' });
    },
    onRefresh: () => {
      // Guard against double-press stacking a second request: `useMutation`
      // does not dedupe concurrent `mutate()` calls the way `useQuery` dedupes
      // in-flight fetches, so this check is load-bearing, not defensive dead
      // code — the button's own `disabled={isRefreshing}` covers the mouse
      // click, this covers any other trigger (e.g. a fast double Enter).
      if (refreshMutation.isPending) return;
      refreshMutation.mutate();
    },
    isRefreshing: refreshMutation.isPending,
    refreshError: refreshMutation.isError,
  };
}
